<div class="w-full mx-auto min-h-screen p-5 bg-gray-50" *ngIf="participantForm">
  <!-- Header Section -->
  <div class="bg-white rounded-lg shadow-sm p-6 mb-6">
    <div class="flex justify-between items-center">
      <div>
        <h1 class="text-3xl font-bold text-gray-900 mb-2">
          {{ participantId ? 'Editar Participante' : 'Crear Nuevo Participante' }}
        </h1>
        <p class="text-gray-600">
          {{ participantId ? 'Modifica la información del participante' : 'Complete la información del nuevo
          participante' }}
        </p>
      </div>
      <div class="flex items-center space-x-3">
        <!-- Auto-generate button - Only visible in non-production environments -->
        <button *ngIf="!isProductionEnvironment && !participantId" type="button" (click)="generateRandomParticipant()"
          class="px-4 py-2 bg-amber-500 text-white rounded-md hover:bg-amber-600 transition-colors flex items-center"
          title="Generar datos aleatorios para pruebas">
          <i class="fas fa-random mr-2"></i>Generar Datos de Prueba
        </button>
        <button type="button" (click)="goBack()"
          class="px-4 py-2 bg-gray-500 text-white rounded-md hover:bg-gray-600 transition-colors">
          <i class="fas fa-arrow-left mr-2"></i>Volver
        </button>
      </div>
    </div>
  </div>

  <!-- Form Section -->
  <div class="bg-white rounded-lg shadow-sm p-6">
    <form [formGroup]="participantForm" (ngSubmit)="onSubmit()" class="space-y-6">
      <!-- Información Personal -->
      <div class="border border-gray-200 rounded-lg p-6 mb-6">
        <h2 class="text-xl font-semibold text-gray-900 mb-4 flex items-center">
          <i class="fas fa-user mr-2 text-blue-500"></i>Información Personal
        </h2>

        <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label for="firstName" class="block text-sm font-medium text-gray-700 mb-2" appRequiredLabel>Nombres</label>
            <input type="text" id="firstName" formControlName="firstName"
              class="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
              placeholder="Ingrese los nombres">
            <div *ngIf="participantForm.get('firstName')?.invalid && participantForm.get('firstName')?.touched"
              class="text-red-500 text-sm mt-1 flex items-center">
              <i class="fas fa-exclamation-circle mr-1"></i>
              El campo Nombres es obligatorio.
            </div>
          </div>

          <div>
            <label for="lastName" class="block text-sm font-medium text-gray-700 mb-2"
              appRequiredLabel>Apellidos</label>
            <input type="text" id="lastName" formControlName="lastName"
              class="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
              placeholder="Ingrese los apellidos">
            <div *ngIf="participantForm.get('lastName')?.invalid && participantForm.get('lastName')?.touched"
              class="text-red-500 text-sm mt-1 flex items-center">
              <i class="fas fa-exclamation-circle mr-1"></i>
              El campo Apellidos es obligatorio.
            </div>
          </div>
        </div>
      </div>

      <!-- Información de Identificación -->
      <div class="border border-gray-200 rounded-lg p-6 mb-6">
        <h2 class="text-xl font-semibold text-gray-900 mb-4 flex items-center">
          <i class="fas fa-id-card mr-2 text-green-500"></i>Información de Identificación
        </h2>

        <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label for="typeDocument" class="block text-sm font-medium text-gray-700 mb-2" appRequiredLabel>Tipo de
              Documento</label>
            <select id="typeDocument" formControlName="typeDocument"
              class="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors">
              <option value="" disabled>Seleccione un tipo de documento</option>
              <option *ngFor="let type of documentTypes" [value]="type.key">{{ type.value }}</option>
            </select>
            <div *ngIf="participantForm.get('typeDocument')?.invalid && participantForm.get('typeDocument')?.touched"
              class="text-red-500 text-sm mt-1 flex items-center">
              <i class="fas fa-exclamation-circle mr-1"></i>
              Debe seleccionar un tipo de documento.
            </div>
          </div>

          <div>
            <label for="documentId" class="block text-sm font-medium text-gray-700 mb-2" appRequiredLabel>Número de
              Documento</label>
            <input type="text" id="documentId" formControlName="documentId"
              class="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
              placeholder="Ingrese el número de documento">
            <div *ngIf="participantForm.get('documentId')?.invalid && participantForm.get('documentId')?.touched"
              class="text-red-500 text-sm mt-1 flex items-center">
              <i class="fas fa-exclamation-circle mr-1"></i>
              El campo Número de Documento es obligatorio.
            </div>
          </div>
        </div>

        <div class="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
          <div>
            <label for="expeditionPlace" class="block text-sm font-medium text-gray-700 mb-2" appRequiredLabel>Lugar de
              Expedición</label>
            <input type="text" id="expeditionPlace" formControlName="expeditionPlace"
              class="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
              placeholder="Ciudad donde se expidió el documento">
            <div
              *ngIf="participantForm.get('expeditionPlace')?.invalid && participantForm.get('expeditionPlace')?.touched"
              class="text-red-500 text-sm mt-1 flex items-center">
              <i class="fas fa-exclamation-circle mr-1"></i>
              El campo Lugar de Expedición es obligatorio.
            </div>
          </div>

          <div>
            <label for="age" class="block text-sm font-medium text-gray-700 mb-2">Edad</label>
            <input type="number" id="age" formControlName="age" min="16" max="100"
              class="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
              placeholder="Edad en años">
          </div>
        </div>
      </div>

      <!-- Información de Contacto -->
      <div class="border border-gray-200 rounded-lg p-6 mb-6">
        <h2 class="text-xl font-semibold text-gray-900 mb-4 flex items-center">
          <i class="fas fa-envelope mr-2 text-purple-500"></i>Información de Contacto
        </h2>

        <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label for="email" class="block text-sm font-medium text-gray-700 mb-2" appRequiredLabel>Correo
              Electrónico</label>
            <input type="email" id="email" formControlName="email"
              class="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
              placeholder="correo@ejemplo.com">
            <div *ngIf="participantForm.get('email')?.invalid && participantForm.get('email')?.touched"
              class="text-red-500 text-sm mt-1 flex items-center">
              <i class="fas fa-exclamation-circle mr-1"></i>
              <span *ngIf="participantForm.get('email')?.errors?.['required']">El campo Correo Electrónico es
                obligatorio.</span>
              <span
                *ngIf="participantForm.get('email')?.errors?.['email'] && !participantForm.get('email')?.errors?.['required']">El
                correo debe tener un formato válido.</span>
            </div>
          </div>

          <div>
            <label for="cellPhone" class="block text-sm font-medium text-gray-700 mb-2" appRequiredLabel>Celular</label>
            <input type="tel" id="cellPhone" formControlName="cellPhone"
              class="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
              placeholder="+57 300 123 4567">
            <div *ngIf="participantForm.get('cellPhone')?.invalid && participantForm.get('cellPhone')?.touched"
              class="text-red-500 text-sm mt-1 flex items-center">
              <i class="fas fa-exclamation-circle mr-1"></i>
              El campo Celular es obligatorio.
            </div>
          </div>
        </div>

        <div class="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
          <div>
            <label for="phone" class="block text-sm font-medium text-gray-700 mb-2">Teléfono Fijo</label>
            <input type="tel" id="phone" formControlName="phone"
              class="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
              placeholder="(1) 234 5678">
          </div>

          <div>
            <label for="profession" class="block text-sm font-medium text-gray-700 mb-2">Profesión</label>
            <input type="text" id="profession" formControlName="profession"
              class="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
              placeholder="Ej: Ingeniero, Médico, Abogado">
          </div>
        </div>
      </div>

      <!-- Información de Ubicación -->
      <div class="border border-gray-200 rounded-lg p-6 mb-6">
        <h2 class="text-xl font-semibold text-gray-900 mb-4 flex items-center">
          <i class="fas fa-map-marker-alt mr-2 text-red-500"></i>Información de Ubicación
        </h2>

        <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label for="country" class="block text-sm font-medium text-gray-700 mb-2" appRequiredLabel>País</label>
            <input type="text" id="country" formControlName="country"
              class="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
              placeholder="Colombia">
            <div *ngIf="participantForm.get('country')?.invalid && participantForm.get('country')?.touched"
              class="text-red-500 text-sm mt-1 flex items-center">
              <i class="fas fa-exclamation-circle mr-1"></i>
              El campo País es obligatorio.
            </div>
          </div>

          <div>
            <label for="city" class="block text-sm font-medium text-gray-700 mb-2" appRequiredLabel>Ciudad</label>
            <input type="text" id="city" formControlName="city"
              class="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
              placeholder="Bogotá">
            <div *ngIf="participantForm.get('city')?.invalid && participantForm.get('city')?.touched"
              class="text-red-500 text-sm mt-1 flex items-center">
              <i class="fas fa-exclamation-circle mr-1"></i>
              El campo Ciudad es obligatorio.
            </div>
          </div>
        </div>

        <div class="mt-6">
          <label for="addressLine1" class="block text-sm font-medium text-gray-700 mb-2" appRequiredLabel>Dirección
            Principal</label>
          <input type="text" id="addressLine1" formControlName="addressLine1"
            class="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
            placeholder="Calle 123 # 45-67">
          <div *ngIf="participantForm.get('addressLine1')?.invalid && participantForm.get('addressLine1')?.touched"
            class="text-red-500 text-sm mt-1 flex items-center">
            <i class="fas fa-exclamation-circle mr-1"></i>
            El campo Dirección Principal es obligatorio.
          </div>
        </div>

        <div class="mt-6">
          <label for="addressLine2" class="block text-sm font-medium text-gray-700 mb-2">Dirección
            Complementaria</label>
          <input type="text" id="addressLine2" formControlName="addressLine2"
            class="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
            placeholder="Apartamento, oficina, edificio (opcional)">
        </div>
      </div>

      <!-- Información de Referencia -->
      <div class="border border-gray-200 rounded-lg p-6 mb-6">
        <h2 class="text-xl font-semibold text-gray-900 mb-4 flex items-center">
          <i class="fas fa-users mr-2 text-orange-500"></i>Información de Referencia
        </h2>
        <p class="text-sm text-gray-600 mb-6">Persona de contacto en caso de emergencia</p>

        <div class="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div>
            <label for="referenceName" class="block text-sm font-medium text-gray-700 mb-2" appRequiredLabel>Nombre
              Completo</label>
            <input type="text" id="referenceName" formControlName="referenceName"
              class="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
              placeholder="Nombre del contacto de referencia">
            <div *ngIf="participantForm.get('referenceName')?.invalid && participantForm.get('referenceName')?.touched"
              class="text-red-500 text-sm mt-1 flex items-center">
              <i class="fas fa-exclamation-circle mr-1"></i>
              El campo Nombre Completo es obligatorio.
            </div>
          </div>

          <div>
            <label for="referenceRelationship" class="block text-sm font-medium text-gray-700 mb-2"
              appRequiredLabel>Parentesco/Relación</label>
            <input type="text" id="referenceRelationship" formControlName="referenceRelationship"
              class="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
              placeholder="Ej: Padre, Madre, Cónyuge, Amigo">
            <div
              *ngIf="participantForm.get('referenceRelationship')?.invalid && participantForm.get('referenceRelationship')?.touched"
              class="text-red-500 text-sm mt-1 flex items-center">
              <i class="fas fa-exclamation-circle mr-1"></i>
              El campo Parentesco/Relación es obligatorio.
            </div>
          </div>

          <div>
            <label for="referencePhoneNumber" class="block text-sm font-medium text-gray-700 mb-2"
              appRequiredLabel>Número de Contacto</label>
            <input type="tel" id="referencePhoneNumber" formControlName="referencePhoneNumber"
              class="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
              placeholder="+57 300 123 4567">
            <div
              *ngIf="participantForm.get('referencePhoneNumber')?.invalid && participantForm.get('referencePhoneNumber')?.touched"
              class="text-red-500 text-sm mt-1 flex items-center">
              <i class="fas fa-exclamation-circle mr-1"></i>
              El campo Número de Contacto es obligatorio.
            </div>
          </div>
        </div>
      </div>

      <!-- Estado del Participante -->
      <div class="border border-gray-200 rounded-lg p-6 mb-6">
        <h2 class="text-xl font-semibold text-gray-900 mb-4 flex items-center">
          <i class="fas fa-user-graduate mr-2 text-indigo-500"></i>Estado del Participante
        </h2>
        <p class="text-sm text-gray-600 mb-6">Define el estado académico actual del participante</p>

        <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label for="status" class="block text-sm font-medium text-gray-700 mb-2">Estado Académico</label>
            <select id="status" formControlName="status"
              class="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors">
              <option value="">Seleccionar estado (opcional)</option>
              <option *ngFor="let status of statusOptions" [value]="status.value">
                {{ status.label }}
              </option>
            </select>
            <p class="text-xs text-gray-500 mt-2">
              <i class="fas fa-info-circle mr-1"></i>
              El estado puede ser definido más tarde si no está disponible ahora
            </p>
          </div>

          <!-- Preview del status seleccionado -->
          <div *ngIf="participantForm.get('status')?.value" class="flex items-center">
            <div class="bg-gray-50 rounded-lg p-4 w-full">
              <div class="text-sm text-gray-600 mb-2">Estado Seleccionado:</div>
              <span class="inline-flex px-3 py-1 text-sm font-semibold rounded-full bg-blue-100 text-blue-800">
                {{ getStatusLabel(participantForm.get('status')?.value) }}
              </span>
            </div>
          </div>
        </div>
      </div>

      <!-- Botones de Acción -->
      <div class="flex justify-end space-x-4 pt-6 border-t border-gray-200">
        <button type="button" (click)="goBack()"
          class="px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors">
          Cancelar
        </button>
        <button type="submit" [disabled]="!participantForm.valid"
          [class]="participantForm.valid ? 'bg-blue-500 hover:bg-blue-600' : 'bg-gray-300 cursor-not-allowed'"
          class="px-6 py-3 text-white rounded-lg transition-colors flex items-center">
          <i class="fas fa-save mr-2"></i>
          {{ participantId ? 'Actualizar Participante' : 'Crear Participante' }}
        </button>
      </div>
    </form>
  </div>
</div>